import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';

// ✅ Auto-sanitized: removes any trailing '/api' or '/'
// ✅ Clean standard React CRA resolution
const rawApiUrl = process.env.REACT_APP_API_BASE_URL || '';

const API_BASE_URL = rawApiUrl.replace(/\/+$/, '').replace(/\/api$/, '');

// Helper to resolve image paths (handles relative, local uploads, and remote URLs)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanPath}`;
};

// Helper to convert crop area to base64
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL('image/jpeg', 0.92);
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const shadowHostRef = useRef(null);

  // Active Tab: 'events' | 'books' | 'awards' | 'scholars' | 'alumni' | 'askme' | 'security'
  const [activeTab, setActiveTab] = useState('events');

  // Events State
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [localizingImages, setLocalizingImages] = useState(false);
  const editingEventRef = useRef(null);
  const existingImagesRef = useRef([]);

  // Books State
  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);
  const [editingBook, setEditingBook] = useState(null);
  const editingBookRef = useRef(null);

  // Awards State
  const [awards, setAwards] = useState([]);
  const [loadingAwards, setLoadingAwards] = useState(true);
  const [editingAward, setEditingAward] = useState(null);
  const [existingAwardImages, setExistingAwardImages] = useState([]);
  const editingAwardRef = useRef(null);
  const existingAwardImagesRef = useRef([]);

  // Doctoral Scholars (Current) State
  const [scholars, setScholars] = useState([]);
  const [loadingScholars, setLoadingScholars] = useState(true);
  const [editingScholarId, setEditingScholarId] = useState(null);
  const [scholarName, setScholarName] = useState('');
  const [scholarTopic, setScholarTopic] = useState('');
  const [scholarSummary, setScholarSummary] = useState('');
  const [scholarImage, setScholarImage] = useState('');

  // Graduated Alumni Scholars State
  const [alumniScholars, setAlumniScholars] = useState([]);
  const [loadingAlumni, setLoadingAlumni] = useState(true);
  const [editingAlumniId, setEditingAlumniId] = useState(null);
  const [alumniName, setAlumniName] = useState('');
  const [alumniTopic, setAlumniTopic] = useState('');
  const [alumniPosition, setAlumniPosition] = useState('');
  const [alumniImage, setAlumniImage] = useState('');

  // Ask Me / Q&A Inquiries State
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [replyTextMap, setReplyTextMap] = useState({});

  // 1:1 Cropper State
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropTarget, setCropTarget] = useState('current');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Security State
  const [adminUser, setAdminUser] = useState('admin');
  const [adminEmail, setAdminEmail] = useState('');
  const [secMsg, setSecMsg] = useState('');
  const [secStep, setSecStep] = useState(1);

  // Feedback State
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    editingEventRef.current = editingEvent;
    existingImagesRef.current = existingImages;
    editingBookRef.current = editingBook;
    editingAwardRef.current = editingAward;
    existingAwardImagesRef.current = existingAwardImages;
  }, [editingEvent, existingImages, editingBook, editingAward, existingAwardImages]);

  // Fetch all dashboard datasets
  const fetchAllData = async () => {
    try {
      const [evRes, bkRes, awRes, scRes, alRes, profRes, qRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/events`),
        fetch(`${API_BASE_URL}/api/books`),
        fetch(`${API_BASE_URL}/api/awards`),
        fetch(`${API_BASE_URL}/api/scholars`),
        fetch(`${API_BASE_URL}/api/alumni-scholars`),
        fetch(`${API_BASE_URL}/api/admin/profile`),
        fetch(`${API_BASE_URL}/api/ask-me`),
      ]);

      if (evRes.ok) {
        const evData = await evRes.json();
        setEvents(Array.isArray(evData) ? evData : []);
      }
      if (bkRes.ok) {
        const bkData = await bkRes.json();
        setBooks(Array.isArray(bkData) ? bkData : []);
      }
      if (awRes.ok) {
        const awData = await awRes.json();
        setAwards(Array.isArray(awData) ? awData : []);
      }
      if (scRes.ok) {
        const scData = await scRes.json();
        setScholars(Array.isArray(scData) ? scData : []);
      }
      if (alRes.ok) {
        const alData = await alRes.json();
        setAlumniScholars(Array.isArray(alData) ? alData : []);
      }
      if (profRes.ok) {
        const profData = await profRes.json();
        if (profData.admin) {
          setAdminUser(profData.admin.username || 'admin');
          setAdminEmail(profData.admin.email || '');
        }
      }
      if (qRes && qRes.ok) {
        const qData = await qRes.json();
        const qArr = Array.isArray(qData) ? qData : [];
        setQuestions(qArr);

        const map = {};
        qArr.forEach((item) => {
          map[item._id] = item.reply || '';
        });
        setReplyTextMap(map);
      }
    } catch (err) {
      console.error('Fetch dashboard data error:', err);
    } finally {
      setLoadingEvents(false);
      setLoadingBooks(false);
      setLoadingAwards(false);
      setLoadingScholars(false);
      setLoadingAlumni(false);
      setLoadingQuestions(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Isolated Shadow DOM for Events, Books, Awards, Security
  useEffect(() => {
    if (!shadowHostRef.current) return;

    let shadowRoot = shadowHostRef.current.shadowRoot;
    if (!shadowRoot) {
      shadowRoot = shadowHostRef.current.attachShadow({ mode: 'open' });
    }

    if (activeTab === 'scholars' || activeTab === 'alumni' || activeTab === 'askme') {
      shadowRoot.innerHTML = '';
      return;
    }

    shadowRoot.innerHTML = `
      <style>
        * { box-sizing: border-box; font-family: system-ui, -apple-system, sans-serif; }
        .wrapper { color: #f8fafc; width: 100%; max-width: 900px; margin: 0 auto; }
        .card { background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 20px; margin-bottom: 20px; }
        .title { color: #f8fafc; font-size: 18px; font-weight: 600; margin-top: 0; margin-bottom: 16px; }
        .grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 768px) { .grid { grid-template-columns: 2fr 1fr; gap: 16px; } .full-width { grid-column: span 2; } }
        .field { display: flex; flex-direction: column; margin-bottom: 10px; }
        label { font-size: 13px; color: #94a3b8; margin-bottom: 6px; }
        input, textarea { width: 100%; padding: 10px; background: #0f172a; border: 1px solid #475569; border-radius: 6px; color: #fff; font-size: 14px; outline: none; }
        input:focus, textarea:focus { border-color: #38bdf8; }
        .btn-group { display: flex; gap: 10px; margin-top: 10px; }
        button { padding: 10px 18px; border-radius: 6px; font-weight: 600; font-size: 14px; border: none; cursor: pointer; }
        .btn-blue { background: #2563eb; color: #fff; }
        .btn-blue:hover { background: #1d4ed8; }
        .btn-cancel { background: #475569; color: #f8fafc; }
        .btn-cancel:hover { background: #334155; }
        .btn-green { background: #059669; color: #fff; width: 100%; }
        .btn-green:hover { background: #047857; }
        .btn-warn { background: #ffc107; color: #000; width: 100%; letter-spacing: 0.7px; font-weight: bold; }
        .btn-warn:hover { background: #ffca2c; }
        .otp-input { font-size: 20px; text-align: center; letter-spacing: 4px; border-color: #38bdf8; }
      </style>

      <div class="wrapper">
        ${
          activeTab === 'events'
            ? `
            <div class="card">
              <div class="title" id="form-heading">Create New Event</div>
              <form id="ev-form">
                <div class="grid">
                  <div class="field">
                    <label>Event Title *</label>
                    <input type="text" id="ev-title" required />
                  </div>
                  <div class="field">
                    <label>Date *</label>
                    <input type="date" id="ev-date" required value="2026-08-23" />
                  </div>
                  <div class="field full-width">
                    <label>
                      Description (HTML tags allowed) *
                      <br/>(Tags: &lt;b&gt;bold&lt;/b&gt;, &lt;br/&gt;line break)
                    </label>
                    <textarea id="ev-desc" rows="3" required></textarea>
                  </div>
                  <div class="field full-width">
                    <label id="file-label">Event Images *</label>
                    <input type="file" id="ev-files" multiple accept="image/*" />
                  </div>
                </div>
                <div class="btn-group">
                  <button type="submit" class="btn-blue" id="submit-btn">Publish Event</button>
                  <button type="button" class="btn-cancel" id="cancel-btn" style="display:none;">Cancel</button>
                </div>
              </form>
            </div>
          `
            : activeTab === 'books'
            ? `
            <div class="card">
              <div class="title" id="bk-heading">Add Published Book</div>
              <form id="bk-form">
                <div class="grid">
                  <div class="field">
                    <label>Book Title *</label>
                    <input type="text" id="bk-title" required placeholder="e.g. Strategic Management" />
                  </div>
                  <div class="field">
                    <label>Publication Year *</label>
                    <input type="text" id="bk-year" required placeholder="e.g. 2026" />
                  </div>
                  <div class="field">
                    <label>Authors *</label>
                    <input type="text" id="bk-authors" required value="Prof. Sanjay Dhir" />
                  </div>
                  <div class="field">
                    <label>Publisher *</label>
                    <input type="text" id="bk-publisher" required placeholder="e.g. Springer / McGraw Hill" />
                  </div>
                  <div class="field">
                    <label>ISBN (Optional)</label>
                    <input type="text" id="bk-isbn" placeholder="e.g. 978-3-030-xxxx-x" />
                  </div>
                  <div class="field">
                    <label>Purchase / Publisher Link (Optional)</label>
                    <input type="url" id="bk-link" placeholder="https://..." />
                  </div>
                  <div class="field full-width">
                    <label>Short Description</label>
                    <textarea id="bk-desc" rows="2" placeholder="Summary or overview..."></textarea>
                  </div>
                  <div class="field full-width">
                    <label id="bk-file-label">Book Cover Image</label>
                    <input type="file" id="bk-file" accept="image/*" />
                  </div>
                </div>
                <div class="btn-group">
                  <button type="submit" class="btn-blue" id="bk-submit-btn">Save Book</button>
                  <button type="button" class="btn-cancel" id="bk-cancel-btn" style="display:none;">Cancel</button>
                </div>
              </form>
            </div>
          `
            : activeTab === 'awards'
            ? `
            <div class="card">
              <div class="title" id="aw-heading">Add New Award / Distinction</div>
              <form id="aw-form">
                <div class="grid">
                  <div class="field">
                    <label>Year / Duration *</label>
                    <input type="text" id="aw-year" required placeholder="e.g. 2026" />
                  </div>
                  <div class="field">
                    <label>Display Order (0 = Top)</label>
                    <input type="number" id="aw-order" value="0" />
                  </div>
                  <div class="field full-width">
                    <label>Category / Eyebrow (Uppercase) *</label>
                    <input type="text" id="aw-category" required placeholder="e.g. GLOBAL SCIENTIST RANKING" />
                  </div>
                  <div class="field full-width">
                    <label>Award Title *</label>
                    <input type="text" id="aw-title" required placeholder="e.g. Research.com Best Business & Management Scientists." />
                  </div>
                  <div class="field full-width">
                    <label>Description *</label>
                    <textarea id="aw-desc" rows="3" required placeholder="Details about this award or recognition..."></textarea>
                  </div>
                  <div class="field full-width">
                    <label id="aw-file-label">Award Images / Badges (Optional)</label>
                    <input type="file" id="aw-files" multiple accept="image/*" />
                  </div>
                </div>
                <div class="btn-group">
                  <button type="submit" class="btn-blue" id="aw-submit-btn">Publish Award</button>
                  <button type="button" class="btn-cancel" id="aw-cancel-btn" style="display:none;">Cancel</button>
                </div>
              </form>
            </div>
          `
            : `
            <div class="card" style="max-width: 480px; margin: 0 auto;">
              <div class="title">Admin Credentials & Security</div>
              <div class="field">
                <label>Username</label>
                <input type="text" value="${adminUser}" disabled style="opacity:0.7;" />
              </div>
              <div class="field">
                <label>Registered Email</label>
                <input type="email" value="${adminEmail || 'No email set'}" disabled style="opacity:0.7;" />
              </div>

              <hr style="border:0; border-top:1px solid #334155; margin:16px 0;" />

              <div class="title" style="font-size:16px;">Update Password via OTP</div>
              ${
                secStep === 1
                  ? `
                  <p style="font-size:13px; color:#94a3b8; margin-bottom:12px;">Generate OTP to change your account password.</p>
                  <button type="button" id="btn-request-otp" class="btn-warn">Send Verification OTP</button>
                `
                  : `
                  <form id="sec-form">
                    <div class="field">
                      <label>6-Digit OTP</label>
                      <input type="text" id="sec-otp" maxlength="6" class="otp-input" placeholder="••••••" required />
                    </div>
                    <div class="field">
                      <label>New Password</label>
                      <input type="password" id="sec-newpass" required placeholder="Enter new password" />
                    </div>
                    <button type="submit" class="btn-green" style="margin-top:10px;">Verify & Save</button>
                  </form>
                `
              }
            </div>
          `
        }
      </div>
    `;

    // Bind Event Handlers
    if (activeTab === 'events') {
      const evForm = shadowRoot.getElementById('ev-form');
      const cancelBtn = shadowRoot.getElementById('cancel-btn');

      const handleEventSubmit = async (e) => {
        e.preventDefault();
        const title = shadowRoot.getElementById('ev-title')?.value?.trim();
        const date = shadowRoot.getElementById('ev-date')?.value;
        const desc = shadowRoot.getElementById('ev-desc')?.value?.trim();
        const fileInput = shadowRoot.getElementById('ev-files');

        if (!title || !date || !desc) return;

        const currentEditing = editingEventRef.current;
        const currentImagesList = existingImagesRef.current;

        const formData = new FormData();
        formData.append('title', title);
        formData.append('date', date);
        formData.append('description', desc);

        if (currentEditing) {
          currentImagesList.forEach((img) => formData.append('existingImages', img));
        }

        if (fileInput && fileInput.files) {
          for (let i = 0; i < fileInput.files.length; i++) {
            formData.append('images', fileInput.files[i]);
          }
        }

        try {
          const url = currentEditing
            ? `${API_BASE_URL}/api/events/${currentEditing._id}`
            : `${API_BASE_URL}/api/events`;
          const method = currentEditing ? 'PUT' : 'POST';

          const res = await fetch(url, { method, body: formData });
          if (res.ok) {
            setFeedback(currentEditing ? 'Event updated successfully!' : 'Event published successfully!');
            resetEventFormState();
            fetchAllData();
          } else {
            setFeedback('Failed to save event');
          }
        } catch (err) {
          setFeedback('Upload error: ' + err.message);
        }
      };

      const handleCancel = (e) => {
        e.preventDefault();
        resetEventFormState();
      };

      evForm?.addEventListener('submit', handleEventSubmit);
      cancelBtn?.addEventListener('click', handleCancel);

      return () => {
        evForm?.removeEventListener('submit', handleEventSubmit);
        cancelBtn?.removeEventListener('click', handleCancel);
      };
    } else if (activeTab === 'books') {
      const bkForm = shadowRoot.getElementById('bk-form');
      const cancelBtn = shadowRoot.getElementById('bk-cancel-btn');

      const handleBookSubmit = async (e) => {
        e.preventDefault();
        const title = shadowRoot.getElementById('bk-title')?.value?.trim();
        const year = shadowRoot.getElementById('bk-year')?.value?.trim();
        const authors = shadowRoot.getElementById('bk-authors')?.value?.trim();
        const publisher = shadowRoot.getElementById('bk-publisher')?.value?.trim();
        const isbn = shadowRoot.getElementById('bk-isbn')?.value?.trim();
        const buyLink = shadowRoot.getElementById('bk-link')?.value?.trim();
        const desc = shadowRoot.getElementById('bk-desc')?.value?.trim();
        const fileInput = shadowRoot.getElementById('bk-file');

        if (!title || !year || !publisher) return;

        const currentEditing = editingBookRef.current;
        const formData = new FormData();
        formData.append('title', title);
        formData.append('year', year);
        formData.append('authors', authors);
        formData.append('publisher', publisher);
        formData.append('isbn', isbn);
        formData.append('buyLink', buyLink);
        formData.append('description', desc);

        if (currentEditing && currentEditing.coverImage) {
          formData.append('existingCover', currentEditing.coverImage);
        }

        if (fileInput && fileInput.files && fileInput.files[0]) {
          formData.append('coverImage', fileInput.files[0]);
        }

        try {
          const url = currentEditing
            ? `${API_BASE_URL}/api/books/${currentEditing._id}`
            : `${API_BASE_URL}/api/books`;
          const method = currentEditing ? 'PUT' : 'POST';

          const res = await fetch(url, { method, body: formData });
          if (res.ok) {
            setFeedback(currentEditing ? 'Book updated successfully!' : 'Book added successfully!');
            resetBookFormState();
            fetchAllData();
          } else {
            setFeedback('Failed to save book');
          }
        } catch (err) {
          setFeedback('Save error: ' + err.message);
        }
      };

      const handleCancel = (e) => {
        e.preventDefault();
        resetBookFormState();
      };

      bkForm?.addEventListener('submit', handleBookSubmit);
      cancelBtn?.addEventListener('click', handleCancel);

      return () => {
        bkForm?.removeEventListener('submit', handleBookSubmit);
        cancelBtn?.removeEventListener('click', handleCancel);
      };
    } else if (activeTab === 'awards') {
      const awForm = shadowRoot.getElementById('aw-form');
      const cancelBtn = shadowRoot.getElementById('aw-cancel-btn');

      const handleAwardSubmit = async (e) => {
        e.preventDefault();
        const year = shadowRoot.getElementById('aw-year')?.value?.trim();
        const order = shadowRoot.getElementById('aw-order')?.value || 0;
        const category = shadowRoot.getElementById('aw-category')?.value?.trim();
        const title = shadowRoot.getElementById('aw-title')?.value?.trim();
        const desc = shadowRoot.getElementById('aw-desc')?.value?.trim();
        const fileInput = shadowRoot.getElementById('aw-files');

        if (!year || !category || !title || !desc) return;

        const currentEditing = editingAwardRef.current;
        const currentImagesList = existingAwardImagesRef.current;

        const formData = new FormData();
        formData.append('year', year);
        formData.append('order', order);
        formData.append('category', category);
        formData.append('title', title);
        formData.append('description', desc);

        if (currentEditing) {
          currentImagesList.forEach((img) => formData.append('existingImages', img));
        }

        if (fileInput && fileInput.files) {
          for (let i = 0; i < fileInput.files.length; i++) {
            formData.append('images', fileInput.files[i]);
          }
        }

        try {
          const url = currentEditing
            ? `${API_BASE_URL}/api/awards/${currentEditing._id}`
            : `${API_BASE_URL}/api/awards`;
          const method = currentEditing ? 'PUT' : 'POST';

          const res = await fetch(url, { method, body: formData });
          if (res.ok) {
            setFeedback(currentEditing ? 'Award updated successfully!' : 'Award added successfully!');
            resetAwardFormState();
            fetchAllData();
          } else {
            setFeedback('Failed to save award');
          }
        } catch (err) {
          setFeedback('Save error: ' + err.message);
        }
      };

      const handleCancel = (e) => {
        e.preventDefault();
        resetAwardFormState();
      };

      awForm?.addEventListener('submit', handleAwardSubmit);
      cancelBtn?.addEventListener('click', handleCancel);

      return () => {
        awForm?.removeEventListener('submit', handleAwardSubmit);
        cancelBtn?.removeEventListener('click', handleCancel);
      };
    } else if (activeTab === 'security') {
      const btnReq = shadowRoot.getElementById('btn-request-otp');
      const secForm = shadowRoot.getElementById('sec-form');

      const handleReqOtp = async () => {
        setSecMsg('Sending OTP...');
        try {
          const res = await fetch(`${API_BASE_URL}/api/admin/password-reset/send-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: adminUser }),
          });
          const data = await res.json();
          if (res.ok && data.success) {
            setSecMsg(data.message);
            setSecStep(2);
          } else {
            setSecMsg(data.message || 'Failed to dispatch OTP');
          }
        } catch (err) {
          setSecMsg('Server error');
        }
      };

      const handleSecSubmit = async (e) => {
        e.preventDefault();
        const otp = shadowRoot.getElementById('sec-otp')?.value;
        const newPassword = shadowRoot.getElementById('sec-newpass')?.value;

        try {
          const res = await fetch(`${API_BASE_URL}/api/admin/password-reset/verify-and-change`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: adminUser, otp, newPassword }),
          });
          const data = await res.json();
          if (res.ok && data.success) {
            alert('Password updated successfully!');
            setSecStep(1);
            setSecMsg('');
          } else {
            setSecMsg(data.message || 'Update failed');
          }
        } catch (err) {
          setSecMsg('Server error');
        }
      };

      btnReq?.addEventListener('click', handleReqOtp);
      secForm?.addEventListener('submit', handleSecSubmit);

      return () => {
        btnReq?.removeEventListener('click', handleReqOtp);
        secForm?.removeEventListener('submit', handleSecSubmit);
      };
    }
  }, [activeTab, secStep, adminUser, adminEmail]);

  // Events Form Reset
  const resetEventFormState = () => {
    setEditingEvent(null);
    setExistingImages([]);
    const shadowRoot = shadowHostRef.current?.shadowRoot;
    if (shadowRoot) {
      const heading = shadowRoot.getElementById('form-heading');
      const submitBtn = shadowRoot.getElementById('submit-btn');
      const cancelBtn = shadowRoot.getElementById('cancel-btn');
      const fileLabel = shadowRoot.getElementById('file-label');
      const form = shadowRoot.getElementById('ev-form');
      const evDate = shadowRoot.getElementById('ev-date');

      if (heading) heading.innerText = 'Create New Event';
      if (submitBtn) submitBtn.innerText = 'Publish Event';
      if (cancelBtn) cancelBtn.style.display = 'none';
      if (fileLabel) fileLabel.innerText = 'Event Images *';
      if (form) form.reset();
      if (evDate) evDate.value = '2026-08-23';
    }
  };

  const handleEditEvent = (ev) => {
    setEditingEvent(ev);
    setExistingImages(ev.images || []);
    setFeedback('');

    const shadowRoot = shadowHostRef.current?.shadowRoot;
    if (shadowRoot) {
      shadowRoot.getElementById('form-heading').innerText = 'Edit Event';
      shadowRoot.getElementById('submit-btn').innerText = 'Save Changes';
      shadowRoot.getElementById('cancel-btn').style.display = 'inline-block';
      shadowRoot.getElementById('file-label').innerText = 'Upload Additional Images (Optional)';

      shadowRoot.getElementById('ev-title').value = ev.title || '';
      shadowRoot.getElementById('ev-date').value = ev.date ? ev.date.split('T')[0] : '';
      shadowRoot.getElementById('ev-desc').value = ev.description || '';
      shadowRoot.getElementById('ev-files').value = '';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event and its associated files?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setEvents((prev) => prev.filter((e) => e._id !== id));
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  // Localize Remote Images Handler
  const handleLocalizeEventImages = async (eventId) => {
    if (!eventId) return;
    setLocalizingImages(true);
    setFeedback('Scanning and downloading remote images to server...');
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/localize-images`, {
        method: 'POST',
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setExistingImages(data.images);
        setEvents((prev) =>
          prev.map((ev) => (ev._id === eventId ? { ...ev, images: data.images } : ev))
        );
        setFeedback(data.message || 'Remote images successfully downloaded to your local uploads folder!');
      } else {
        setFeedback(data.error || 'Failed to localize images.');
      }
    } catch (err) {
      setFeedback('Error localizing images: ' + err.message);
    } finally {
      setLocalizingImages(false);
    }
  };

  // Books Form Reset
  const resetBookFormState = () => {
    setEditingBook(null);
    const shadowRoot = shadowHostRef.current?.shadowRoot;
    if (shadowRoot) {
      const heading = shadowRoot.getElementById('bk-heading');
      const submitBtn = shadowRoot.getElementById('bk-submit-btn');
      const cancelBtn = shadowRoot.getElementById('bk-cancel-btn');
      const form = shadowRoot.getElementById('bk-form');

      if (heading) heading.innerText = 'Add Published Book';
      if (submitBtn) submitBtn.innerText = 'Save Book';
      if (cancelBtn) cancelBtn.style.display = 'none';
      if (form) form.reset();
    }
  };

  const handleEditBook = (bk) => {
    setEditingBook(bk);
    setFeedback('');

    const shadowRoot = shadowHostRef.current?.shadowRoot;
    if (shadowRoot) {
      shadowRoot.getElementById('bk-heading').innerText = 'Edit Book Details';
      shadowRoot.getElementById('bk-submit-btn').innerText = 'Save Changes';
      shadowRoot.getElementById('bk-cancel-btn').style.display = 'inline-block';

      shadowRoot.getElementById('bk-title').value = bk.title || '';
      shadowRoot.getElementById('bk-year').value = bk.year || '';
      shadowRoot.getElementById('bk-authors').value = bk.authors || '';
      shadowRoot.getElementById('bk-publisher').value = bk.publisher || '';
      shadowRoot.getElementById('bk-isbn').value = bk.isbn || '';
      shadowRoot.getElementById('bk-link').value = bk.buyLink || '';
      shadowRoot.getElementById('bk-desc').value = bk.description || '';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteBook = async (id) => {
    if (!window.confirm('Delete this book record?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/books/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setBooks((prev) => prev.filter((b) => b._id !== id));
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  // Awards Form Reset
  const resetAwardFormState = () => {
    setEditingAward(null);
    setExistingAwardImages([]);
    const shadowRoot = shadowHostRef.current?.shadowRoot;
    if (shadowRoot) {
      const heading = shadowRoot.getElementById('aw-heading');
      const submitBtn = shadowRoot.getElementById('aw-submit-btn');
      const cancelBtn = shadowRoot.getElementById('aw-cancel-btn');
      const fileLabel = shadowRoot.getElementById('file-label');
      const form = shadowRoot.getElementById('aw-form');

      if (heading) heading.innerText = 'Add New Award / Distinction';
      if (submitBtn) submitBtn.innerText = 'Publish Award';
      if (cancelBtn) cancelBtn.style.display = 'none';
      if (fileLabel) fileLabel.innerText = 'Award Images / Badges (Optional)';
      if (form) form.reset();
    }
  };

  const handleEditAward = (aw) => {
    setEditingAward(aw);
    setExistingAwardImages(aw.images || []);
    setFeedback('');

    const shadowRoot = shadowHostRef.current?.shadowRoot;
    if (shadowRoot) {
      shadowRoot.getElementById('aw-heading').innerText = 'Edit Award Details';
      shadowRoot.getElementById('aw-submit-btn').innerText = 'Save Changes';
      shadowRoot.getElementById('aw-cancel-btn').style.display = 'inline-block';
      shadowRoot.getElementById('aw-file-label').innerText = 'Upload Additional Badges (Optional)';

      shadowRoot.getElementById('aw-year').value = aw.year || '';
      shadowRoot.getElementById('aw-order').value = aw.order !== undefined ? aw.order : 0;
      shadowRoot.getElementById('aw-category').value = aw.category || '';
      shadowRoot.getElementById('aw-title').value = aw.title || '';
      shadowRoot.getElementById('aw-desc').value = aw.description || '';
      shadowRoot.getElementById('aw-files').value = '';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAward = async (id) => {
    if (!window.confirm('Delete this award record?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/awards/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setAwards((prev) => prev.filter((a) => a._id !== id));
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  const handleRemoveExistingImage = (idxToRemove) => {
    setExistingImages((prev) => prev.filter((_, idx) => idx !== idxToRemove));
  };

  const handleRemoveExistingAwardImage = (idxToRemove) => {
    setExistingAwardImages((prev) => prev.filter((_, idx) => idx !== idxToRemove));
  };

  // ==========================================
  //     IMAGE CROPPER WORKFLOW
  // ==========================================
  const triggerCropper = (e, target) => {
    if (e.target.files && e.target.files.length > 0) {
      setCropTarget(target);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setRawImageSrc(reader.result);
        setShowCropper(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onCropCompleteCallback = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirmCrop = async () => {
    try {
      const croppedImage = await createCroppedImage(rawImageSrc, croppedAreaPixels);
      if (cropTarget === 'current') {
        setScholarImage(croppedImage);
      } else {
        setAlumniImage(croppedImage);
      }
      setShowCropper(false);
      setRawImageSrc(null);
    } catch (e) {
      console.error('Crop error:', e);
    }
  };

  // ==========================================
  //     1. CURRENT PURSUING SCHOLARS CRUD
  // ==========================================
  const resetScholarForm = () => {
    setEditingScholarId(null);
    setScholarName('');
    setScholarTopic('');
    setScholarSummary('');
    setScholarImage('');
  };

  const handleScholarSubmit = async (e) => {
    e.preventDefault();
    if (!scholarImage) {
      setFeedback('Please upload and crop a scholar portrait.');
      return;
    }

    const payload = {
      name: scholarName,
      topic: scholarTopic,
      researchSummary: scholarSummary,
      imageUrl: scholarImage,
    };

    try {
      const url = editingScholarId
        ? `${API_BASE_URL}/api/scholars/${editingScholarId}`
        : `${API_BASE_URL}/api/scholars`;
      const method = editingScholarId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setFeedback(editingScholarId ? 'Current scholar updated successfully!' : 'Current scholar added successfully!');
        resetScholarForm();
        fetchAllData();
      } else {
        setFeedback(data.error || 'Failed to save current scholar.');
      }
    } catch (err) {
      setFeedback('Error: ' + err.message);
    }
  };

  const handleEditScholar = (sc) => {
    setEditingScholarId(sc._id);
    setScholarName(sc.name || '');
    setScholarTopic(sc.topic || '');
    setScholarSummary(sc.researchSummary || '');
    setScholarImage(sc.imageUrl || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteScholar = async (id) => {
    if (!window.confirm('Delete this doctoral scholar profile?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/scholars/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setScholars((prev) => prev.filter((s) => s._id !== id));
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  // ==========================================
  //     2. GRADUATED ALUMNI SCHOLARS CRUD
  // ==========================================
  const resetAlumniForm = () => {
    setEditingAlumniId(null);
    setAlumniName('');
    setAlumniTopic('');
    setAlumniPosition('');
    setAlumniImage('');
  };

  const handleAlumniSubmit = async (e) => {
    e.preventDefault();
    if (!alumniImage) {
      setFeedback('Please upload and crop an alumni portrait (1:1 ratio).');
      return;
    }

    const payload = {
      name: alumniName,
      topic: alumniTopic,
      currentPosition: alumniPosition,
      imageUrl: alumniImage,
    };

    try {
      const url = editingAlumniId
        ? `${API_BASE_URL}/api/alumni-scholars/${editingAlumniId}`
        : `${API_BASE_URL}/api/alumni-scholars`;
      const method = editingAlumniId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setFeedback(editingAlumniId ? 'Alumni scholar updated successfully!' : 'Alumni scholar published successfully!');
        resetAlumniForm();
        fetchAllData();
      } else {
        setFeedback(data.error || 'Failed to save alumni scholar');
      }
    } catch (err) {
      setFeedback('Error: ' + err.message);
    }
  };

  const handleEditAlumni = (al) => {
    setEditingAlumniId(al._id);
    setAlumniName(al.name || '');
    setAlumniTopic(al.topic || '');
    setAlumniPosition(al.currentPosition || '');
    setAlumniImage(al.imageUrl || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAlumni = async (id) => {
    if (!window.confirm('Delete this graduated scholar record?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/alumni-scholars/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setAlumniScholars((prev) => prev.filter((a) => a._id !== id));
      }
    } catch (err) {
      alert('Delete failed');
    }
  };

  // ==========================================
  //     3. ASK ME / Q&A MANAGEMENT
  // ==========================================
  const handleReplyChange = (id, val) => {
    setReplyTextMap((prev) => ({ ...prev, [id]: val }));
  };

  const handleSendReply = async (id) => {
    const replyText = replyTextMap[id];
    if (!replyText || !replyText.trim()) {
      alert('Please enter a response before submitting.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/ask-me/${id}/reply`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: replyText }),
      });

      if (res.ok) {
        setFeedback('Reply saved and published to the website!');
        fetchAllData();
      } else {
        setFeedback('Failed to update reply.');
      }
    } catch (err) {
      setFeedback('Error saving reply: ' + err.message);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/ask-me/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setQuestions((prev) => prev.filter((q) => q._id !== id));
        setFeedback('Inquiry deleted successfully.');
      } else {
        setFeedback('Failed to delete question.');
      }
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  // Detect whether an image URL is truly external
  const isImageLocal = (img) => {
    if (!img) return false;
    return (
      img.startsWith(API_BASE_URL) ||
      img.startsWith('/uploads/') ||
      img.includes('/uploads/')
    );
  };

  const externalImageCount = existingImages.filter((img) => !isImageLocal(img)).length;

  return (
    <div className="container py-4 text-light" style={{ maxWidth: '960px' }}>
      
      {/* 1:1 Aspect Ratio Cropper Modal */}
      {showCropper && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center p-3"
          style={{ background: 'rgba(3, 7, 18, 0.94)', zIndex: 3000, backdropFilter: 'blur(10px)' }}
        >
          <div
            className="position-relative rounded-4 overflow-hidden border border-secondary border-opacity-50"
            style={{ width: '100%', maxWidth: '440px', height: '360px', background: '#000' }}
          >
            <Cropper
              image={rawImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={true}
              onCropChange={setCrop}
              onCropComplete={onCropCompleteCallback}
              onZoomChange={setZoom}
            />
          </div>

          <div className="w-100 text-center my-3" style={{ maxWidth: '440px' }}>
            <label className="text-secondary small d-block mb-1">Adjust Zoom</label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="form-range"
            />
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              onClick={handleConfirmCrop}
              className="btn btn-info text-dark fw-bold px-4 rounded-pill"
            >
              Confirm 1:1 Crop
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCropper(false);
                setRawImageSrc(null);
              }}
              className="btn btn-outline-secondary px-4 rounded-pill text-light"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Top Navbar */}
      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom border-secondary pb-3">
        <div>
          <h3 className="fw-bold text-white mb-0">Admin Control Center</h3>
        </div>
        <button onClick={handleLogout} className="btn btn-outline-danger btn-sm">
          Logout
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'events' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('events');
            setFeedback('');
          }}
        >
          Manage Events
        </button>

        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'books' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('books');
            setFeedback('');
          }}
        >
          Manage Books
        </button>

        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'awards' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('awards');
            setFeedback('');
          }}
        >
          Manage Awards
        </button>

        {/* Tab 4: Current Scholars */}
        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'scholars' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('scholars');
            setFeedback('');
          }}
        >
          Doctoral Scholars (Current)
        </button>

        {/* Tab 5: Graduated Alumni Scholars */}
        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'alumni' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('alumni');
            setFeedback('');
          }}
        >
          Doctoral Scholars (Alumni)
        </button>

        {/* Tab 6: Manage Q&A */}
        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'askme' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('askme');
            setFeedback('');
          }}
        >
          Ask Me / Q&A ({questions.length})
        </button>

        <button
          type="button"
          className={`btn btn-sm px-3 ${
            activeTab === 'security' ? 'btn-primary text-white fw-bold' : 'btn-dark text-secondary border-secondary'
          }`}
          onClick={() => {
            setActiveTab('security');
            setFeedback('');
          }}
        >
          User & Security
        </button>
      </div>

      {/* Status Alerts */}
      {feedback && <div className="alert alert-info py-2 small mb-3">{feedback}</div>}
      {secMsg && activeTab === 'security' && <div className="alert alert-info py-2 small mb-3">{secMsg}</div>}

      {/* Existing Images Gallery during Event Edit Mode */}
      {activeTab === 'events' && editingEvent && existingImages.length > 0 && (
        <div className="card bg-dark border-secondary p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div>
              <label className="form-label small text-secondary mb-0">Currently Attached Images ({existingImages.length})</label>
              {externalImageCount > 0 && (
                <span className="badge bg-warning text-dark ms-2 small">
                  {externalImageCount} Remote / External URL(s)
                </span>
              )}
            </div>

            {externalImageCount > 0 && (
              <button
                type="button"
                disabled={localizingImages}
                onClick={() => handleLocalizeEventImages(editingEvent._id)}
                className="btn btn-sm btn-info text-dark fw-bold px-3 rounded-pill"
              >
                {localizingImages ? 'Downloading Images...' : `📥 Download ${externalImageCount} Remote Images to Local Server`}
              </button>
            )}
          </div>

          <div className="d-flex flex-wrap gap-2">
            {existingImages.map((img, idx) => {
              const isLocal = isImageLocal(img);
              return (
                <div key={idx} className="position-relative border border-secondary rounded p-1 text-center" style={{ width: '85px' }}>
                  <img src={getImageUrl(img)} alt="" style={{ width: '75px', height: '55px', objectFit: 'cover' }} className="rounded" />
                  <span
                    className={`badge position-absolute bottom-0 start-0 m-1 ${isLocal ? 'bg-success' : 'bg-warning text-dark'}`}
                    style={{ fontSize: '9px', padding: '2px 4px' }}
                  >
                    {isLocal ? 'Local' : 'Web'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(idx)}
                    className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0"
                    style={{ width: '20px', height: '20px', fontSize: '11px', lineHeight: '1' }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Existing Images Gallery during Award Edit Mode */}
      {activeTab === 'awards' && editingAward && existingAwardImages.length > 0 && (
        <div className="card bg-dark border-secondary p-3 mb-3">
          <label className="form-label small text-secondary">Currently Uploaded Badges / Images</label>
          <div className="d-flex flex-wrap gap-2">
            {existingAwardImages.map((img, idx) => (
              <div key={idx} className="position-relative border border-secondary rounded p-1">
                <img src={getImageUrl(img)} alt="" style={{ width: '75px', height: '55px', objectFit: 'cover' }} className="rounded" />
                <button
                  type="button"
                  onClick={() => handleRemoveExistingAwardImage(idx)}
                  className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0"
                  style={{ width: '20px', height: '20px', fontSize: '11px', lineHeight: '1' }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unified Shadow DOM Host (Events / Books / Awards / Security) */}
      <div ref={shadowHostRef} />

      {/* TAB 4: CURRENT PURSUING SCHOLARS VIEW */}
      {activeTab === 'scholars' && (
        <div className="mt-2">
          <div
            className="p-4 rounded-4 mb-4 border border-secondary border-opacity-25"
            style={{ background: '#1e293b' }}
          >
            <h5 className="text-white fw-bold mb-3">
              {editingScholarId ? 'Edit Pursuing Scholar Profile' : 'Add Current Doctoral Scholar (/DoctoralSection)'}
            </h5>
            <form onSubmit={handleScholarSubmit}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Scholar Name *</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-light border-secondary"
                    value={scholarName}
                    onChange={(e) => setScholarName(e.target.value)}
                    required
                    placeholder="e.g. Nehal Jain"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Research Topic / Title</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-light border-secondary"
                    value={scholarTopic}
                    onChange={(e) => setScholarTopic(e.target.value)}
                    placeholder="e.g. Equity and Inclusivity in Healthcare"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label small text-secondary">Research Summary / Abstract</label>
                  <textarea
                    className="form-control bg-dark text-light border-secondary"
                    rows="3"
                    value={scholarSummary}
                    onChange={(e) => setScholarSummary(e.target.value)}
                    placeholder="Detailed abstract, methodology, or questions..."
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">
                    Profile Photo * (1:1 Ratio Crop)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => triggerCropper(e, 'current')}
                    className="form-control bg-dark text-light border-secondary"
                  />
                </div>

                {scholarImage && (
                  <div className="col-12 col-md-6 d-flex align-items-center gap-3">
                    <img
                      src={getImageUrl(scholarImage)}
                      alt="Cropped Preview"
                      className="rounded-circle border border-2 border-info shadow"
                      style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                    />
                    <span className="small text-secondary">1:1 Circular preview ready</span>
                  </div>
                )}
              </div>

              <div className="mt-4 d-flex gap-2">
                <button type="submit" className="btn btn-primary text-white fw-bold px-4 rounded-pill">
                  {editingScholarId ? 'Update Scholar' : 'Publish to /DoctoralSection'}
                </button>
                {editingScholarId && (
                  <button
                    type="button"
                    onClick={resetScholarForm}
                    className="btn btn-outline-secondary px-3 rounded-pill text-light"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <h5 className="text-white mb-3">Published Current Scholars ({scholars.length})</h5>
          {loadingScholars ? (
            <p className="text-secondary">Loading records...</p>
          ) : scholars.length === 0 ? (
            <p className="text-secondary">No pursuing scholars added yet.</p>
          ) : (
            <div className="table-responsive rounded border border-secondary">
              <table className="table table-dark table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: '70px' }}>Photo</th>
                    <th>Name</th>
                    <th>Research Area</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {scholars.map((sc) => (
                    <tr key={sc._id}>
                      <td>
                        <img
                          src={getImageUrl(sc.imageUrl)}
                          alt={sc.name}
                          className="rounded-circle border border-info"
                          style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                        />
                      </td>
                      <td className="fw-semibold text-white">{sc.name}</td>
                      <td className="text-secondary small">{sc.topic || 'No topic specified'}</td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            onClick={() => handleEditScholar(sc)}
                            className="btn btn-sm btn-outline-warning"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteScholar(sc._id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 5: GRADUATED ALUMNI VIEW */}
      {activeTab === 'alumni' && (
        <div className="mt-2">
          <div
            className="p-4 rounded-4 mb-4 border border-secondary border-opacity-25"
            style={{ background: '#1e293b' }}
          >
            <h5 className="text-white fw-bold mb-3">
              {editingAlumniId ? 'Edit Alumni Scholar Profile' : 'Add Graduated Alumni Scholar (/Research/DoctoralScholars)'}
            </h5>
            <form onSubmit={handleAlumniSubmit}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Scholar Name *</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-light border-secondary"
                    value={alumniName}
                    onChange={(e) => setAlumniName(e.target.value)}
                    required
                    placeholder="e.g. Dr. Nisha Bamel"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Current Placement / Institution *</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-light border-secondary"
                    value={alumniPosition}
                    onChange={(e) => setAlumniPosition(e.target.value)}
                    required
                    placeholder="e.g. BIMTECH / Commodore-Indian Navy / IIT Hyderabad"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label small text-secondary">Doctoral Thesis Title</label>
                  <textarea
                    className="form-control bg-dark text-light border-secondary"
                    rows="2"
                    value={alumniTopic}
                    onChange={(e) => setAlumniTopic(e.target.value)}
                    placeholder="e.g. Inter-Firm Dynamics of Joint Venture Partner: Strategic Flexibility and Joint Venture Competitiveness"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">
                    Profile Photo * (1:1 Ratio Crop)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => triggerCropper(e, 'alumni')}
                    className="form-control bg-dark text-light border-secondary"
                  />
                </div>

                {alumniImage && (
                  <div className="col-12 col-md-6 d-flex align-items-center gap-3">
                    <img
                      src={getImageUrl(alumniImage)}
                      alt="Cropped Preview"
                      className="rounded-circle border border-2 border-warning shadow"
                      style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                    />
                    <span className="small text-secondary">1:1 Circular preview ready</span>
                  </div>
                )}
              </div>

              <div className="mt-4 d-flex gap-2">
                <button type="submit" className="btn btn-warning text-dark fw-bold px-4 rounded-pill">
                  {editingAlumniId ? 'Update Scholar' : 'Publish to /Research/DoctoralScholars'}
                </button>
                {editingAlumniId && (
                  <button
                    type="button"
                    onClick={resetAlumniForm}
                    className="btn btn-outline-secondary px-3 rounded-pill text-light"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <h5 className="text-white mb-3">Published Graduated Alumni ({alumniScholars.length})</h5>
          {loadingAlumni ? (
            <p className="text-secondary">Loading records...</p>
          ) : alumniScholars.length === 0 ? (
            <p className="text-secondary">No graduated scholars added yet.</p>
          ) : (
            <div className="table-responsive rounded border border-secondary">
              <table className="table table-dark table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: '70px' }}>Photo</th>
                    <th>Name</th>
                    <th>Current Placement</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {alumniScholars.map((sc) => (
                    <tr key={sc._id}>
                      <td>
                        <img
                          src={getImageUrl(sc.imageUrl)}
                          alt={sc.name}
                          className="rounded-circle border border-warning"
                          style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                        />
                      </td>
                      <td className="fw-semibold text-white">{sc.name}</td>
                      <td className="text-secondary small">{sc.currentPosition}</td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            onClick={() => handleEditAlumni(sc)}
                            className="btn btn-sm btn-outline-warning"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAlumni(sc._id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 6: MANAGE Q&A / ASK ME */}
      {activeTab === 'askme' && (
        <div className="mt-2">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-white mb-0">Community Questions & Public Queries ({questions.length})</h5>
            <span className="badge bg-dark border border-secondary text-info">
              {questions.filter((q) => !q.reply).length} Awaiting Response
            </span>
          </div>

          {loadingQuestions ? (
            <p className="text-secondary">Loading questions...</p>
          ) : questions.length === 0 ? (
            <div className="p-4 rounded-4 border border-secondary border-opacity-25 text-center" style={{ background: '#1e293b' }}>
              <p className="text-secondary mb-0">No questions have been submitted by visitors yet.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {questions.map((q) => (
                <div
                  key={q._id}
                  className="p-4 rounded-4 border border-secondary border-opacity-25"
                  style={{
                    background: '#1e293b',
                    borderLeft: q.reply ? '4px solid #38bdf8' : '4px solid #f59e0b',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <strong className="text-white fs-6">{q.name}</strong>
                      {q.email && <span className="text-secondary small ms-2">({q.email})</span>}
                      <div className="text-secondary small" style={{ fontSize: '0.75rem' }}>
                        Submitted on {new Date(q.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge rounded-pill small"
                        style={{
                          background: q.reply ? 'rgba(56, 189, 248, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                          color: q.reply ? '#38bdf8' : '#f59e0b',
                          border: q.reply ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)',
                        }}
                      >
                        {q.reply ? 'Answered' : 'Pending'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteQuestion(q._id)}
                        className="btn btn-sm btn-outline-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <p className="text-light p-3 rounded-3 bg-dark border border-secondary border-opacity-25 my-3">
                    "{q.question}"
                  </p>

                  <div className="mt-3">
                    <label className="form-label small text-info fw-semibold">
                      Your Official Response (Prof. Sanjay Dhir):
                    </label>
                    <textarea
                      className="form-control bg-dark text-light border-secondary mb-2"
                      rows="3"
                      placeholder="Type your academic or advisory guidance here..."
                      value={replyTextMap[q._id] || ''}
                      onChange={(e) => handleReplyChange(q._id, e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => handleSendReply(q._id)}
                      className="btn btn-sm btn-info text-dark fw-bold px-3 rounded-pill"
                    >
                      {q.reply ? 'Update Response' : 'Publish Response'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 1: Events Table */}
      {activeTab === 'events' && (
        <div className="mt-4">
          <h5 className="text-white mb-3">Published Events ({events.length})</h5>
          {loadingEvents ? (
            <p className="text-secondary">Loading records...</p>
          ) : (
            <div className="table-responsive rounded border border-secondary">
              <table className="table table-dark table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Title</th>
                    <th className="text-center">Images</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev._id}>
                      <td className="text-secondary small">{new Date(ev.date).toLocaleDateString()}</td>
                      <td className="fw-semibold text-white">{ev.title}</td>
                      <td className="text-center">
                        <span className="badge bg-secondary">{ev.images?.length || 0}</span>
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            onClick={() => handleEditEvent(ev)}
                            className="btn btn-sm btn-outline-warning"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteEvent(ev._id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: Books Table */}
      {activeTab === 'books' && (
        <div className="mt-4">
          <h5 className="text-white mb-3">Published Books ({books.length})</h5>
          {loadingBooks ? (
            <p className="text-secondary">Loading records...</p>
          ) : (
            <div className="table-responsive rounded border border-secondary">
              <table className="table table-dark table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Cover</th>
                    <th>Title</th>
                    <th>Year</th>
                    <th>Publisher</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((bk) => (
                    <tr key={bk._id}>
                      <td>
                        {bk.coverImage ? (
                          <img
                            src={getImageUrl(bk.coverImage)}
                            alt=""
                            style={{ width: '38px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <span className="badge bg-secondary">No Cover</span>
                        )}
                      </td>
                      <td className="fw-semibold text-white">{bk.title}</td>
                      <td className="text-info">{bk.year}</td>
                      <td className="text-secondary small">{bk.publisher}</td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            onClick={() => handleEditBook(bk)}
                            className="btn btn-sm btn-outline-warning"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBook(bk._id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: Awards Table */}
      {activeTab === 'awards' && (
        <div className="mt-4">
          <h5 className="text-white mb-3">Published Awards & Distinctions ({awards.length})</h5>
          {loadingAwards ? (
            <p className="text-secondary">Loading records...</p>
          ) : awards.length === 0 ? (
            <p className="text-secondary">No awards added yet.</p>
          ) : (
            <div className="table-responsive rounded border border-secondary">
              <table className="table table-dark table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Order</th>
                    <th>Year</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th className="text-center">Badges</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {awards.map((aw) => (
                    <tr key={aw._id}>
                      <td className="text-secondary small">{aw.order ?? 0}</td>
                      <td>
                        <span className="badge bg-warning text-dark fw-bold">{aw.year}</span>
                      </td>
                      <td className="fw-semibold text-white">{aw.title}</td>
                      <td className="text-info small">{aw.category}</td>
                      <td className="text-center">
                        <span className="badge bg-secondary">{aw.images?.length || 0}</span>
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            onClick={() => handleEditAward(aw)}
                            className="btn btn-sm btn-outline-warning"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAward(aw._id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}